import { useEffect, useState } from 'react'
import Screen from '../components/ui/Screen'
import TopBar from '../components/ui/TopBar'
import BottomNav from '../components/ui/BottomNav'
import Panel from '../components/ui/Panel'
import PickSteps from '../components/ui/PickSteps'
import PickSegment from '../components/ui/PickSegment'
import Stepper from '../components/ui/Stepper'
import texts from '../texts'
import { parseWhistScorecardCsv } from '../lib/scorecard'
import type { ScorecardTable } from '../lib/scorecard'
import { isLoggedIn } from '../lib/api'

const LEVELS = [7, 8, 9, 10, 11, 12, 13] as const
const VARIANTS = ['Almindelige', 'Halve', 'Gode', '1. Vip', '2. Vip', '3. Vip'] as const
const SOLO_TYPES = ['Sol', 'Ren sol', 'Ren bordlægger', 'Dobbelt bordlægger'] as const

type Variant = (typeof VARIANTS)[number]
type SoloType = (typeof SOLO_TYPES)[number]

const SOLO_VALUE: Record<SoloType, number> = {
  Sol: 24,
  'Ren sol': 48,
  'Ren bordlægger': 192,
  'Dobbelt bordlægger': 384
}
const BASE: Record<number, number> = { 7: 1, 8: 2, 9: 4, 10: 8, 11: 16, 12: 32, 13: 64 }
const MULT: Record<Variant, number> = {
  Almindelige: 1,
  Halve: 2,
  Gode: 2,
  '1. Vip': 2,
  '2. Vip': 4,
  '3. Vip': 8
}

/** Point til melder ved en given melding (positivt tal, negativt hvis meldingen ikke går hjem). */
const doublesInClubs = (variant: Variant) => variant === 'Halve' || variant.includes('Vip')

function bidScore(input: { level: number; variant: Variant; tricks: number; solo: SoloType | null; won: boolean; clubs: boolean }): number {
  if (input.solo) return input.won ? SOLO_VALUE[input.solo] : -SOLO_VALUE[input.solo]
  const clubsFactor = input.clubs && doublesInClubs(input.variant) ? 2 : 1
  const V = BASE[input.level] * MULT[input.variant] * clubsFactor
  const d = input.tricks - input.level
  if (d >= 0) return V * (d + 1)
  return input.variant === 'Almindelige' ? V * (d - 1) : V * d
}

function fmt(n: number): string {
  return (n > 0 ? '+' : '') + n
}

function scoreClass(n: number): string {
  return n > 0 ? 'text-mint' : n < 0 ? 'text-red' : ''
}

function Rules() {
  const [level, setLevel] = useState<number>(9)
  const [variant, setVariant] = useState<Variant>('Gode')
  const [tricks, setTricks] = useState(8)
  const [solo, setSolo] = useState<SoloType | null>(null)
  const [won, setWon] = useState(true)
  const [clubs, setClubs] = useState(false)
  const [tables, setTables] = useState<ScorecardTable[]>([])
  const [tablesLoading, setTablesLoading] = useState(true)
  const [tablesError, setTablesError] = useState('')
  const loggedIn = isLoggedIn()

  useEffect(() => {
    fetch('/whist_scorecard.csv', { cache: 'no-store' })
      .then((res) => {
        if (!res.ok) throw new Error('Kunne ikke hente CSV')
        return res.text()
      })
      .then((text) => setTables(parseWhistScorecardCsv(text)))
      .catch(() => setTablesError(texts.rules.tablesError))
      .finally(() => setTablesLoading(false))
  }, [])

  const score = bidScore({ level, variant, tricks, solo, won, clubs })

  const handleLevelChange = (l: number) => {
    setSolo(null)
    setLevel(l)
    setTricks((t) => Math.max(t, l))
  }

  return (
    <Screen>
      <TopBar title={texts.rules.title} back="/" />

      <Panel>
        <span className="font-mono text-[0.72rem] uppercase tracking-[0.08em] text-text-2">
          {texts.rules.calculatorLabel}
        </span>

        <PickSteps
          options={[
            ...LEVELS.map((l) => ({ value: l, label: String(l) })),
            { value: -1, label: texts.rules.solo, wide: true }
          ]}
          isActive={(opt) => (opt.value === -1 ? !!solo : !solo && opt.value === level)}
          onChange={(v) => (v === -1 ? setSolo((s) => s || 'Sol') : handleLevelChange(v))}
        />

        {solo ? (
          <>
            <PickSegment
              options={SOLO_TYPES.map((s) => ({ value: s, label: s.replace('bordlægger', 'bordl.') }))}
              value={solo}
              onChange={setSolo}
            />
            <PickSegment
              options={[
                { value: true, label: texts.rules.won },
                { value: false, label: texts.rules.lost }
              ]}
              value={won}
              onChange={setWon}
            />
          </>
        ) : (
          <>
            <PickSegment
              options={VARIANTS.map((v) => ({ value: v, label: v.replace('Almindelige', 'Alm.') }))}
              value={variant}
              onChange={setVariant}
            />
            {doublesInClubs(variant) && (
              <PickSegment
                options={[
                  { value: false, label: texts.rules.trumpOther },
                  { value: true, label: texts.rules.trumpClubs }
                ]}
                value={clubs}
                onChange={setClubs}
              />
            )}
            <Stepper value={tricks} onChange={setTricks} min={0} max={13} label={texts.rules.tricksTaken} />
          </>
        )}

        <p className="min-h-[1.4em] text-center text-[1.1em] text-text-2">
          {solo ? (
            <>
              {texts.rules.soloBidder} <b className={`font-mono ${scoreClass(score)}`}>{fmt(score)}</b> ·{' '}
              {texts.rules.soloOthers}{' '}
              <b className={`font-mono ${scoreClass(-score)}`}>{fmt(Math.round(-score / 3))}</b>{' '}
              {texts.rules.each}
            </>
          ) : (
            <>
              {texts.rules.bidderAndPartner} <b className={`font-mono ${scoreClass(score)}`}>{fmt(score)}</b>{' '}
              {texts.rules.each} · {texts.rules.opponents}{' '}
              <b className={`font-mono ${scoreClass(-score)}`}>{fmt(-score)}</b> {texts.rules.each}
            </>
          )}
        </p>
      </Panel>

      <Panel>
        <b>{texts.rules.explanationTitle}</b>
        <p className="text-[0.85em] text-text-2">{texts.rules.explanationP1}</p>
        <p className="text-[0.85em] text-text-2">{texts.rules.explanationP2}</p>
      </Panel>

      <span className="font-mono text-[0.72rem] uppercase tracking-[0.08em] text-text-2">
        {texts.rules.tablesTitle}
      </span>
      <p className="text-[0.85em] text-text-2">{texts.rules.tablesHint}</p>

      {tablesLoading ? (
        <p className="text-text-2">{texts.rules.tablesLoading}</p>
      ) : tablesError ? (
        <p className="text-red">{tablesError}</p>
      ) : tables.length === 0 ? (
        <p className="text-text-2">{texts.rules.tablesEmpty}</p>
      ) : (
        tables.map((t) => (
          <Panel key={t.title}>
            <b>{t.title}</b>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="whitespace-nowrap px-2.5 py-1.5 text-left font-mono text-[0.8em] font-bold text-text-2">
                      {t.rowHeader}
                    </th>
                    {t.colHeaders.map((h) => (
                      <th
                        key={h}
                        className="whitespace-nowrap px-2.5 py-1.5 text-right font-mono text-[0.8em] font-bold text-text-2"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {t.rows.map((r) => (
                    <tr key={r.label} className="border-t border-text-2/20">
                      <td className="whitespace-nowrap px-2.5 py-1.5 font-mono font-bold">{r.label}</td>
                      {r.values.map((v, idx) => {
                        const num = Number(v)
                        const isNumber = v.trim().length > 0 && Number.isFinite(num)
                        const display = isNumber && num > 0 ? `+${num}` : v
                        return (
                          <td
                            key={idx}
                            className={`whitespace-nowrap px-2.5 py-1.5 text-right font-mono tabular-nums ${scoreClass(
                              isNumber ? num : 0
                            )}`}
                          >
                            {display}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        ))
      )}

      {loggedIn && <BottomNav current="me" />}
    </Screen>
  )
}

export default Rules
